import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SecurityContext,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { NavBarItem } from '@shared/models/nav-bar-item.model';
import { LayoutService } from '@layout/services/layout.service';
import { PopoverDirective } from '@shared/directives/popover-directive';
import { DomSanitizer } from '@angular/platform-browser';
import { IsActiveMatchOptions, NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

// Same matching as the items' `routerLinkActive` with `exact: false`.
const CHILD_ACTIVE_MATCH: IsActiveMatchOptions = {
  paths: 'subset',
  queryParams: 'ignored',
  fragment: 'ignored',
  matrixParams: 'ignored',
};

@Component({
  selector: 'app-nav-list',
  templateUrl: './nav-list.component.html',
  styleUrls: ['./nav-list.component.scss'],
})
export class NavListComponent implements OnChanges, OnInit, OnDestroy {
  @Input() color: 'primary' | 'basic' = 'basic';
  @Input() menuList: NavBarItem[];
  @Input() collapsed = false;
  @Input() divider = false;
  @Output() action = new EventEmitter();
  @ViewChildren('popoverInstance') popovers: QueryList<PopoverDirective>;
  position: ConnectedPosition = {
    originX: 'end',
    originY: 'top',
    overlayX: 'start',
    overlayY: 'top',
  };

  protected htmlItemsMap: Record<string, string> = {};

  activeParents = new Set<NavBarItem>();

  private readonly destroy$ = new Subject<void>();

  constructor(
    public layoutService: LayoutService,
    private sanitizer: DomSanitizer,
    private router: Router,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnChanges({ menuList }: SimpleChanges): void {
    if (menuList?.currentValue) {
      this.htmlItemsMap = {};
      this.menuList.forEach((item) => {
        const isHtml = /<[a-z][\s\S]*>/i.test(item.title);
        if (!isHtml) return;
        this.htmlItemsMap[item.title] = this.sanitizer.sanitize(SecurityContext.HTML, item.title);
      });
    }

    if (menuList) {
      this.updateActiveParents();
    }
  }

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.updateActiveParents());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  stopPropagation(event: Event): void {
    event.stopPropagation();
  }

  handleOpenPopover(popover: PopoverDirective): void {
    this.popovers.forEach((element) => {
      if (element.overlayRef.hasAttached() && element !== popover) {
        element.close();
      }
    });
  }

  closeAllPopovers(): void {
    this.handleOpenPopover(null);
  }

  private updateActiveParents(): void {
    this.activeParents = new Set((this.menuList || []).filter((item) => this.isChildActive(item)));
    this.changeDetectorRef.markForCheck();
  }

  // On the collapsed rail a parent has no label, so it is highlighted when one of
  // its submodules is the current page.
  private isChildActive(menuItem: NavBarItem): boolean {
    return !!menuItem.subMenu?.some((submenu) => submenu.path && this.router.isActive('/' + submenu.path, CHILD_ACTIVE_MATCH));
  }

  // Some titles are HTML; the tooltip shows their text only.
  tooltipText(menuItem: NavBarItem): string {
    return (menuItem.title ?? '').replace(/<[^>]*>/g, '').trim();
  }
}
